import { useEffect, useState } from "react";

const DEFAULT_COPY_PROMPT =
  "Please summarize our conversation for my Context Savior project manager. Focus on: tech stack decisions, key decisions made, constraints established, and open questions still unresolved. Keep it concise and structured — no code blocks or small talk.";
const SUMMARY_PLACEHOLDER =
  "Paste the key parts of your conversation here — focus on decisions made, constraints agreed on, tech choices, and questions that got resolved. Skip code blocks, error messages, and small talk. Tip: use the \"Copy summary prompt\" button above to ask your AI for a pre-formatted summary first.";

const SummarySection = ({
  isOpen,
  onToggle,
  conversationText,
  onConversationChange,
  summaryError,
  isSummarizing,
  onGenerateSummary,
  copyPrompt,
}) => {
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    if (!promptCopied) return undefined;
    const timer = setTimeout(() => setPromptCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [promptCopied]);

  const handleCopyPrompt = async () => {
    const text = copyPrompt || DEFAULT_COPY_PROMPT;
    try {
      await navigator.clipboard.writeText(text);
      setPromptCopied(true);
    } catch (error) {
      const fallback = document.createElement("textarea");
      fallback.value = text;
      fallback.setAttribute("readonly", "");
      fallback.style.position = "absolute";
      fallback.style.left = "-9999px";
      document.body.appendChild(fallback);
      fallback.select();
      document.execCommand("copy");
      document.body.removeChild(fallback);
      setPromptCopied(true);
    }
  };

  return (
    <section className="pcm-summary" data-testid="summary-section">
      <button
        type="button"
        className="pcm-summary-toggle"
        onClick={onToggle}
        data-testid="summary-toggle-button"
      >
        ✨ Already have an AI conversation? Import it here
      </button>

      {isOpen ? (
        <div className="pcm-summary-body" data-testid="summary-body">
          <label
            htmlFor="summary-conversation"
            className="pcm-label"
            data-testid="summary-label"
          >
            AI Summary
          </label>
          <textarea
            id="summary-conversation"
            className="pcm-textarea pcm-summary-textarea"
            placeholder={SUMMARY_PLACEHOLDER}
            value={conversationText}
            onChange={onConversationChange}
            data-testid="summary-conversation-textarea"
          />
          <p className="pcm-summary-tip" data-testid="summary-tip">
            Tip: Ask your AI to summarize your conversation first using the{" "}
            <strong>Copy summary prompt</strong> button below for best results.
          </p>
          <div className="pcm-summary-prompt" data-testid="summary-prompt-row">
            <button
              type="button"
              className="pcm-summary-prompt-button"
              onClick={handleCopyPrompt}
              data-testid="summary-copy-prompt-button"
            >
              📋 Copy summary prompt
            </button>
            {promptCopied ? (
              <span
                className="pcm-summary-prompt-confirmation"
                data-testid="summary-prompt-copied-message"
              >
                Prompt copied!
              </span>
            ) : null}
          </div>
          {summaryError ? (
            <div className="pcm-summary-error" data-testid="summary-error-message">
              {summaryError}
            </div>
          ) : null}
          <button
            type="button"
            className="pcm-button pcm-button-primary pcm-summary-button"
            onClick={onGenerateSummary}
            disabled={isSummarizing}
            data-testid="generate-summary-button"
          >
            {isSummarizing ? "Summarizing..." : "Generate Summary"}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default SummarySection;